import React from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, Select, TextArea, DateInput } from '@origo/origo-ui';
import type { ProcessingFormValues, SelectOption } from '../../../types';
import FormFooterActions from '../../plan-info/components/FormFooterActions';
import styles from './ProcessingForm.module.css';

export interface ProcessingFormOptions {
    planbehandlingtype: SelectOption[];
}

export type ProcessingFieldErrors = Partial<Record<keyof ProcessingFormValues, string>>;

interface ProcessingFormProps {
    values: ProcessingFormValues;
    options: ProcessingFormOptions;
    fieldErrors?: ProcessingFieldErrors;
    /** Vises read-only når skjemaet redigerer en eksisterende behandling (spec F5.1.5). */
    planbehUid?: string;
    onChange: (field: keyof ProcessingFormValues, value: string) => void;
    /** Skjemaet rendrer egen knapperad når `onSave`/`onCancel` er gitt;
     *  dialoger som eier footeren selv lar dem være undefined. */
    onCancel?: () => void;
    onSave?: () => void;
    onDelete?: () => void;
    saveDisabled?: boolean;
    isSaving?: boolean;
}

const ProcessingForm: React.FC<ProcessingFormProps> = ({
    values,
    options,
    fieldErrors = {},
    planbehUid,
    onChange,
    onCancel,
    onSave,
    onDelete,
    saveDisabled,
    isSaving,
}) => {
    const { t } = useTranslation();

    return (
        <div className={styles.form}>
            <TextInput
                label={t('processings.fields.navn')}
                placeholder={t('processings.fields.navnPlaceholder')}
                value={values.navn}
                error={fieldErrors.navn}
                onChange={e => onChange('navn', e.target.value)}
            />
            <Select
                label={t('processings.fields.planbehandlingtype')}
                placeholder={t('processings.fields.planbehandlingtypePlaceholder')}
                value={values.planbehandlingtype}
                error={fieldErrors.planbehandlingtype}
                onChange={e => onChange('planbehandlingtype', e.target.value)}
                options={options.planbehandlingtype}
            />
            <div className={styles.twoColumn}>
                <DateInput
                    label={t('processings.fields.dato')}
                    value={values.dato}
                    error={fieldErrors.dato}
                    onChange={e => onChange('dato', e.target.value)}
                />
                <TextInput
                    label={t('processings.fields.saksnummer')}
                    placeholder={t('processings.fields.saksnummerPlaceholder')}
                    value={values.saksnummer}
                    error={fieldErrors.saksnummer}
                    onChange={e => onChange('saksnummer', e.target.value)}
                />
            </div>
            <div className={styles.twoColumn}>
                <DateInput
                    label={t('processings.fields.fradato')}
                    helperText={t('processings.fields.periodeHint')}
                    value={values.fradato}
                    error={fieldErrors.fradato}
                    onChange={e => onChange('fradato', e.target.value)}
                />
                <DateInput
                    label={t('processings.fields.tildato')}
                    value={values.tildato}
                    error={fieldErrors.tildato}
                    onChange={e => onChange('tildato', e.target.value)}
                />
            </div>
            <div className={styles.divider} aria-hidden />
            <TextArea
                label={t('processings.fields.beskrivelse')}
                helperText={t('processings.fields.publicHint')}
                placeholder={t('processings.fields.beskrivelsePlaceholder')}
                value={values.beskrivelse}
                error={fieldErrors.beskrivelse}
                onChange={e => onChange('beskrivelse', e.target.value)}
                rows={4}
            />
            {planbehUid && (
                <p className={styles.meta}>
                    <span className={styles.metaLabel}>{t('processings.fields.planbehUid')}</span>{' '}
                    <code className={styles.metaValue}>{planbehUid}</code>
                </p>
            )}
            {onSave && onCancel && (
                <FormFooterActions
                    onCancel={onCancel}
                    onSave={onSave}
                    onDelete={onDelete}
                    deleteLabel={t('processings.delete.action')}
                    saveDisabled={saveDisabled}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
};

export default ProcessingForm;
